import { atom } from 'recoil';
import toLower from 'lodash/toLower';

import { recoilSelector } from 'common/util/recoil-selector';

import * as K from 'common/constants';
import { exists } from 'common/functions';
import { LogEntry, Query } from 'common/graphqlTypes';
import LogDetails from 'components/common/LogDetails/LogDetails';
import { LRQRequest } from 'hooks/useLRQ/useLRQ.types';

import { plugins } from 'plugins';
import { DataDetailsCardInfo } from 'plugins/types';
import { DATA_SOURCE_NAME_FIELD, DATASOURCE, FIELD_TYPE, findSchema, SchemaFields, schemas } from 'schemas';
import { ResultsTab, View } from 'views/types';
import { views } from 'views';
import { edrView } from 'views/deep-visibility';
import { standardView } from 'views/standard';
import { accountDataState } from 'components/account/account.state';
import { eventParamsState, searchParamState } from './RouteStateManager.state';

export const defaultViewSelector = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.DEFAULT_VIEW_SETTING,
  get: ({ get }) => {
    const account = get(accountDataState);

    if (account.isSingularityXDRUser) {
      return edrView;
    }
    return standardView;
  },
});

/**
 * Default view.
 * This may change to reflect a default view based on user data.
 */
export const defaultViewState = atom<View>({
  key: K.RECOIL_ATOM_KEY.DEFAULT_VIEW,
  default: defaultViewSelector,
});

/**
 * LRQ requests used to calculate the search results count per tab.
 */
export const countPerTabRequestState = atom<LRQRequest | null>({
  key: K.RECOIL_ATOM_KEY.COUNT_PER_TAB_REQUEST,
  default: null,
  dangerouslyAllowMutability: true,
});

// TODO: Figure a way to configure a default view per user
// For instance, if a user has both EDR and XDR products, what should be their
// default view when they go to search page
export const viewState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.VIEW,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const view = views[eventParams[K.URL_PARAM.VIEW]];

    if (view) {
      return view;
    }

    return get(defaultViewState);
  },
});

/**
 * Returns if the current view is the standard view.
 */
export const isStandardViewState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.IS_STANDARD_VIEW,
  get: ({ get }) => {
    const view = get(viewState);

    return view.name === standardView.name;
  },
});

/**
 * Returns the plugin associated with the current view.
 */
export const pluginState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.PLUGIN,
  get: ({ get }) => {
    const view = get(viewState);

    return (view.plugin && plugins[view.plugin]) || null;
  },
});

/**
 * The list of available tabs in the current view.
 */
export const resultsTabsState = recoilSelector<ResultsTab[]>({
  key: K.RECOIL_SELECTOR_KEY.RESULTS_TABS,
  get: ({ get }) => {
    const view = get(viewState);
    const tabs = view.resultTabs || [];

    return tabs.map((tab) => ({
      ...tab,
      displaySettings: {
        ...view.displaySettings,
        ...tab.displaySettings,
      },
    }));
  },
});

/**
 * The current results tab the user is in the current view.
 */
export const currentResultsTabState = recoilSelector<ResultsTab | null>({
  key: K.RECOIL_SELECTOR_KEY.RESULTS_TAB,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const resultsTabs = get(resultsTabsState);

    if (resultsTabs.length === 0) {
      return null;
    }

    const tab = resultsTabs.find(({ id }) => id === eventParams[K.URL_PARAM.RESULTS_TAB]);

    if (tab) {
      return tab;
    }

    return resultsTabs[0];
  },
});

/**
 * Results tabs for current view that contains a filter
 */
export const resultsTabsWithFilterState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.RESULTS_TABS_WITH_FILTER,
  get: ({ get }) => {
    const resultsTabs = get(resultsTabsState);
    return resultsTabs.filter(({ filter }) => Boolean(filter));
  },
});

/**
 * Returns the resolved display settings for the current view and tab.
 */
export const viewDisplaySettingsState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.VIEW_DISPLAY_SETTINGS,
  get: ({ get }) => {
    const view = get(viewState);
    const selectedResultTab = get(currentResultsTabState);

    const displaySettings = {
      ...view.displaySettings,
      ...selectedResultTab?.displaySettings,
    };

    return {
      ...displaySettings,
      // Making sure the column list is unique to avoid inadvertent duplicates
      columns: [...new Set(displaySettings.columns)],
    };
  },
});

/**
 * Map of all the fields for this view, based on its sources and their schemas.
 */
export const viewFieldsState = recoilSelector<SchemaFields>({
  key: K.RECOIL_SELECTOR_KEY.VIEW_FIELDS,
  get: ({ get }) => {
    const view = get(viewState);
    const sources = view.dataSources || [];

    return sources.reduce((acc, source) => ({ ...acc, ...(schemas[source]?.fields || {}) }), {});
  },
});

/**
 * Returns a function to allow to obtain the schema for a given log entry.
 */
export const getSchemaState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.SCHEMA,
  get: () => {
    const getSchema = (logEntry: LogEntry | null, dataSource?: string) => {
      const logEntryDataSource = logEntry && { ...logEntry?.serverInfo, ...logEntry?.values }[DATA_SOURCE_NAME_FIELD];
      if (!dataSource && !logEntryDataSource) {
        return;
      }

      return findSchema(dataSource || logEntryDataSource);
    };

    return getSchema;
  },
});

/**
 * Returns a function that obtains the list of fields for a given log entry in the current view.
 */
export const getMergedSchemaFieldsState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.SCHEMA_FIELDS,
  get: ({ get }) => {
    const getSchema = get(getSchemaState);

    const getMergedSchemaFields = (logEntry: LogEntry | null, useViewSchemas = false) => {
      if (!logEntry && !useViewSchemas) {
        return;
      }
      return useViewSchemas ? get(viewFieldsState) : getSchema(logEntry)?.fields ?? {};
    };

    return getMergedSchemaFields;
  },
});

/**
 * Returns a function to allow to obtain the specific cell renderer for the given field type if
 * configured in the schema.
 */
export const getCellRendererState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.CELL_RENDERER,
  get: ({ get }) => {
    const getMergedSchemaFields = get(getMergedSchemaFieldsState);
    const plugin = get(pluginState);

    const getCellRenderer = (field: string, logEntry: LogEntry | null, useViewSchemas = false) => {
      if (!plugin) {
        return;
      }
      const schemaFields = getMergedSchemaFields(logEntry, useViewSchemas);
      const schemaFieldType = schemaFields?.[field]?.type;
      return schemaFieldType && plugin.cellRenderers[schemaFieldType];
    };

    return getCellRenderer;
  },
});

/**
 * The appropriate component to render log details (either pinned or in the Inspect Drawer) based on the current view and
 * its plugin.
 * Defaults to LogDetails.
 */
export const logDetailsRendererState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.LOG_DETAILS_RENDERER,
  get: ({ get }) => {
    const plugin = get(pluginState);

    return plugin?.inspectDrawerRenderer || LogDetails;
  },
});

/**
 * Data retrieving component as defined within the plugin, per data source.
 */
export const getDetailsDataManagerState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.DETAILS_DATA_MANAGER,
  get: ({ get }) => {
    const plugin = get(pluginState);
    const getDetailsDataManager = (logEntry: LogEntry, dataSource: DATASOURCE) =>
      dataSource && plugin?.detailsDataManager && plugin?.detailsDataManager[dataSource];

    return getDetailsDataManager;
  },
});

/**
 * Event details data that can be optionally retrieved and used to enhance actions or the inspector view.
 */
export const detailsDataState = atom<Partial<Query> | undefined>({
  key: K.RECOIL_ATOM_KEY.DETAILS_DATA,
  default: undefined,
});

/**
 * The appropriate component to render additional data details based on the current view and its plugin.
 * Displayed as an alternative to log details.
 */
export const getCellDataDetailsRendererState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.DATA_DETAILS_CARD_RENDERER,
  get: ({ get }) => {
    const plugin = get(pluginState);

    const getCellDataDetailsRenderer = (field: FIELD_TYPE) => plugin?.cellDataDetailsCardRenderers?.[field];

    return getCellDataDetailsRenderer;
  },
});

/**
 * The list of additional menu items for search results menu defined within a plugin
 */
export const pluginSearchResultsMenuItemsState = recoilSelector({
  key: K.RECOIL_ATOM_KEY.SEARCH_RESULTS_MENU_ITEMS,
  get: ({ get }) => {
    const plugin = get(pluginState);
    return plugin?.searchResultsMenuItems;
  },
});

/**
 * A comma-separated list of the data source values that are currently active (these values are
 * filtering the search results).
 */
export const activeDataSourcesState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.ACTIVE_DATA_SOURCES,
  get: ({ get }) =>
    get(searchParamState(K.URL_PARAM.DATA_SOURCE))
      .split(',')
      .map((v) => findSchema(v)?.dataSourceLabel ?? v)
      .join(','),
});

/**
 * The list of available data source schemas, either from the url or from the view.
 */
export const availableDataSourcesState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.AVAILABLE_DATA_SOURCES,
  get: ({ get }) => {
    const view = get(viewState);
    const valuesFromView = (view.sources?.sourceValues || []).map(toLower);
    const dataSourceParam = get(searchParamState(K.URL_PARAM.DATA_SOURCE));
    const valuesFromUrl = dataSourceParam
      .split(',')
      .map(toLower)
      .filter((v) => valuesFromView.includes(v));
    const availableValues = valuesFromUrl.length > 0 ? valuesFromUrl : valuesFromView;

    return availableValues.map(findSchema).filter(exists);
  },
});

/**
 * Tracks the current state of Data Details Card to display the corresponding component according to the field type
 */
export const dataDetailsCardTriggerState = atom<DataDetailsCardInfo | null>({
  key: K.RECOIL_ATOM_KEY.SHOW_DATA_DETAILS,
  default: null,
});
